/**
 * @orgware/i18n-nationality — core API.
 *
 * Pure lookup helpers + a locale registry. No locales are registered by
 * default; callers either register their own via {@link registerLocale}
 * or import the package's main entry (`@orgware/i18n-nationality`) which
 * auto-registers the bundled de/en/fr/hu locales — see `entry-node.ts`.
 *
 * Ported 1:1 from the original CJS `index.js`. Behavioural semantics
 * (including the try/catch fallbacks in `getName` and `getAlpha2Code`)
 * are preserved exactly so the 70-case test suite passes unchanged.
 */
import { remove as removeDiacritics } from 'diacritics'
import codesData from '../codes.json' with { type: 'json' }

export type Alpha2Code = string
export type Alpha3Code = string
export type NumericCode = string

export type LocalizedNationalityNames = Record<Alpha2Code, string>

export type LocaleData = {
  locale: string
  nationalities: LocalizedNationalityNames
}

type CountryCodeTuple = readonly [Alpha2Code, Alpha3Code, NumericCode, string]

// codes.json is shaped as `[[alpha2, alpha3, numeric, "ISO 3166-2:XX"], ...]`.
// TS sees it as `string[][]` from the JSON import — the cast goes via
// `unknown` because string[][] doesn't overlap a 4-tuple shape directly.
const codes = codesData as unknown as readonly CountryCodeTuple[]
const registeredLocales: Record<string, LocalizedNationalityNames> = {}

// All four lookup directions, computed once at module load.
const alpha2: Record<Alpha2Code, Alpha3Code> = {}
const alpha3: Record<Alpha3Code, Alpha2Code> = {}
const numeric: Record<NumericCode, Alpha2Code> = {}
const invertedNumeric: Record<Alpha2Code, NumericCode> = {}

for (const codeInformation of codes) {
  alpha2[codeInformation[0]] = codeInformation[1]
  alpha3[codeInformation[1]] = codeInformation[0]
  numeric[codeInformation[2]] = codeInformation[0]
  invertedNumeric[codeInformation[0]] = codeInformation[2]
}

// Pad a numeric input to the 3-digit canonical form the lookup maps use.
// Matches the original falsy-coercion behaviour: 0, '', null and undefined
// all collapse to '000'.
function formatNumericCode(code: NumericCode | number | null | undefined): string {
  return String('000' + (code ? code : '')).slice(-3)
}

export function registerLocale(localeData: LocaleData): void {
  if (!localeData.locale) {
    throw new TypeError('Missing localeData.locale')
  }
  if (!localeData.nationalities) {
    throw new TypeError('Missing localeData.nationalities')
  }
  registeredLocales[localeData.locale] = localeData.nationalities
}

/**
 * @param code Alpha-3 code
 * @returns Alpha-2 code or undefined
 */
export function alpha3ToAlpha2(code: Alpha3Code): Alpha2Code | undefined {
  return alpha3[code]
}

/**
 * @param code Alpha-2 code
 * @returns Alpha-3 code or undefined
 */
export function alpha2ToAlpha3(code: Alpha2Code): Alpha3Code | undefined {
  return alpha2[code]
}

/**
 * @param code Alpha-3 code
 * @returns Numeric code or undefined
 */
export function alpha3ToNumeric(code: Alpha3Code): NumericCode | undefined {
  const a2 = alpha3ToAlpha2(code)
  return a2 ? invertedNumeric[a2] : undefined
}

/**
 * @param code Alpha-2 code
 * @returns Numeric code or undefined
 */
export function alpha2ToNumeric(code: Alpha2Code): NumericCode | undefined {
  return invertedNumeric[code]
}

/**
 * @param code Numeric code
 * @returns Alpha-3 code or undefined
 */
export function numericToAlpha3(code: NumericCode | number): Alpha3Code | undefined {
  const padded = formatNumericCode(code)
  const a2 = numeric[padded]
  return a2 ? alpha2ToAlpha3(a2) : undefined
}

/**
 * @param code Numeric code
 * @returns Alpha-2 code or undefined
 */
export function numericToAlpha2(code: NumericCode | number): Alpha2Code | undefined {
  const padded = formatNumericCode(code)
  return numeric[padded]
}

/**
 * @param code ISO 3166-1 alpha-2, alpha-3 or numeric code
 * @returns ISO 3166-1 alpha-3 or undefined
 */
export function toAlpha3(code: string | number): Alpha3Code | undefined {
  if (typeof code === 'string') {
    if (/^[0-9]*$/.test(code)) {
      return numericToAlpha3(code)
    }
    if (code.length === 2) {
      return alpha2ToAlpha3(code.toUpperCase())
    }
    if (code.length === 3) {
      return code.toUpperCase()
    }
  }
  if (typeof code === 'number') {
    return numericToAlpha3(code)
  }
  return undefined
}

/**
 * @param code ISO 3166-1 alpha-2, alpha-3 or numeric code
 * @returns ISO 3166-1 alpha-2 or undefined
 */
export function toAlpha2(code: string | number): Alpha2Code | undefined {
  if (typeof code === 'string') {
    if (/^[0-9]*$/.test(code)) {
      return numericToAlpha2(code)
    }
    if (code.length === 2) {
      return code.toUpperCase()
    }
    if (code.length === 3) {
      return alpha3ToAlpha2(code.toUpperCase())
    }
  }
  if (typeof code === 'number') {
    return numericToAlpha2(code)
  }
  return undefined
}

/**
 * @param code ISO 3166-1 alpha-2, alpha-3 or numeric code
 * @param lang language for nationality name
 * @returns name or undefined
 */
export function getName(code: string | number, lang: string): string | undefined {
  try {
    const d = registeredLocales[lang.toLowerCase()]
    if (!d) return undefined
    const a2 = toAlpha2(code)
    return a2 ? d[a2] : undefined
  } catch {
    return undefined
  }
}

/**
 * @param lang language for nationality names
 * @returns Object of country code mapped to nationality name (empty if locale not registered)
 */
export function getNames(lang: string): LocalizedNationalityNames {
  const d = registeredLocales[lang.toLowerCase()]
  if (d === undefined) {
    return {}
  }
  return d
}

/**
 * @param name name
 * @param lang language for nationality name
 * @returns ISO 3166-1 alpha-2 or undefined
 */
export function getAlpha2Code(name: string, lang: string): Alpha2Code | undefined {
  try {
    const codenames = registeredLocales[lang.toLowerCase()]
    if (!codenames) return undefined
    for (const p of Object.keys(codenames)) {
      const value = codenames[p]
      if (value && value.toLowerCase() === name.toLowerCase()) {
        return p
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

/**
 * Like {@link getAlpha2Code} but compares names with diacritics stripped.
 *
 * @param name name
 * @param lang language for nationality name
 * @returns ISO 3166-1 alpha-2 or undefined
 */
export function getSimpleAlpha2Code(name: string, lang: string): Alpha2Code | undefined {
  try {
    const codenames = registeredLocales[lang.toLowerCase()]
    if (!codenames) return undefined
    for (const p of Object.keys(codenames)) {
      const value = codenames[p]
      if (
        value &&
        removeDiacritics(value.toLowerCase()) === removeDiacritics(name.toLowerCase())
      ) {
        return p
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

/**
 * @returns Object of alpha-2 codes mapped to alpha-3 codes
 */
export function getAlpha2Codes(): Record<Alpha2Code, Alpha3Code> {
  return alpha2
}

/**
 * @param name name
 * @param lang language for nationality name
 * @returns ISO 3166-1 alpha-3 or undefined
 */
export function getAlpha3Code(name: string, lang: string): Alpha3Code | undefined {
  const a2 = getAlpha2Code(name, lang)
  if (a2) {
    return toAlpha3(a2)
  }
  return undefined
}

/**
 * Like {@link getAlpha3Code} but compares names with diacritics stripped.
 *
 * @param name name
 * @param lang language for nationality name
 * @returns ISO 3166-1 alpha-3 or undefined
 */
export function getSimpleAlpha3Code(name: string, lang: string): Alpha3Code | undefined {
  const a2 = getSimpleAlpha2Code(name, lang)
  if (a2) {
    return toAlpha3(a2)
  }
  return undefined
}

/**
 * @returns Object of alpha-3 codes mapped to alpha-2 codes
 */
export function getAlpha3Codes(): Record<Alpha3Code, Alpha2Code> {
  return alpha3
}

/**
 * @returns Object of numeric codes mapped to alpha-2 codes
 */
export function getNumericCodes(): Record<NumericCode, Alpha2Code> {
  return numeric
}

/**
 * @returns Array of registered locale identifiers
 */
export function langs(): string[] {
  return Object.keys(registeredLocales)
}

/**
 * @param code ISO 3166-1 alpha-2, alpha-3 or numeric code
 * @returns true if the input is a recognised code in any form
 */
export function isValid(code: string | number | null | undefined): boolean {
  if (!code) {
    return false
  }
  const coerced = code.toString().toUpperCase()
  return (
    Object.prototype.hasOwnProperty.call(alpha3, coerced) ||
    Object.prototype.hasOwnProperty.call(alpha2, coerced) ||
    Object.prototype.hasOwnProperty.call(numeric, coerced)
  )
}
